"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  CheckboxRow,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
} from "@/components/tools/ui";

/* COSHH assessment builder.
   Structures a substance-by-substance assessment under the Control of Substances
   Hazardous to Health Regulations 2002. The tool frames the Reg.7 hierarchy
   (eliminate/substitute > engineering > administrative > PPE/RPE) but the
   suitability and sufficiency of the assessment remain the competent assessor's
   judgement — this never declares anything "COSHH compliant".

   Domain grounding (RamsDocs knowledge bank, HSE construction hazardous
   substances pages): cement carries hexavalent-chromium sensitisation +
   irritant dermatitis and silica when cut; cutting/grinding masonry releases
   respirable crystalline silica (silicosis/lung cancer); isocyanates (PU foam,
   two-pack paints) are a leading cause of occupational asthma; wood dust causes
   asthma and sino-nasal cancer. Workplace exposure limits live in HSE EH40 and
   SDS Section 8. */

const GHS_HAZARDS = [
  { id: "flammable", label: "Flammable" },
  { id: "corrosive", label: "Corrosive / skin burns" },
  { id: "acute-toxic", label: "Acute toxic" },
  { id: "sensitiser", label: "Health hazard / sensitiser" },
  { id: "carcinogen", label: "Serious health hazard / carcinogen, mutagen" },
  { id: "environmental", label: "Environmental / aquatic" },
] as const;

const ROUTES = [
  { id: "inhalation", label: "Inhalation" },
  { id: "skin", label: "Skin contact / absorption" },
  { id: "eyes", label: "Eyes" },
  { id: "ingestion", label: "Ingestion" },
] as const;

/* Hierarchy-ordered controls: elimination/substitution first, then engineering,
   then administrative, then hygiene. PPE/RPE handled separately below. */
const CONTROLS = [
  { id: "eliminate", label: "Eliminate or substitute the product for a less hazardous one (or a safer form, e.g. ready-mixed, paste, lower-VOC)" },
  { id: "wet-cutting", label: "Wet cutting / dust suppression at source" },
  { id: "lev", label: "LEV or on-tool extraction (M-class, regularly tested)" },
  { id: "enclosure", label: "Enclosure / segregation of the work area" },
  { id: "reduce-time", label: "Reduce exposure time / job rotation / limit quantities in use" },
  { id: "hygiene", label: "Good hygiene — washing facilities, no eating in the work area, skin checks" },
  { id: "spill-kit", label: "Spill kit and bunding available for the quantity stored" },
] as const;

type GhsId = (typeof GHS_HAZARDS)[number]["id"];
type RouteId = (typeof ROUTES)[number]["id"];
type ControlId = (typeof CONTROLS)[number]["id"];

interface Substance {
  id: number;
  product: string;
  hazards: GhsId[];
  routes: RouteId[];
  whoExposed: string;
  welNote: string;
  controls: ControlId[];
  ppeGloves: string;
  ppeEye: string;
  ppeRpe: string;
  storage: string;
  firstAid: string;
  healthSurveillance: boolean;
  healthSurveillanceNote: string;
}

/* Preset defaults. Hazards/routes/controls verified against the knowledge bank
   and HSE construction hazardous-substances guidance. These are sensible starting
   points only — the assessor must replace them with the actual SDS for the product
   in use on this job. */
interface Preset {
  label: string;
  product: string;
  hazards: GhsId[];
  routes: RouteId[];
  welNote: string;
  controls: ControlId[];
  ppeGloves: string;
  ppeEye: string;
  ppeRpe: string;
  storage: string;
  firstAid: string;
  healthSurveillance: boolean;
  healthSurveillanceNote: string;
}

const PRESETS: Preset[] = [
  {
    label: "Cement / concrete / mortar (wet)",
    product: "Cement / concrete / mortar",
    hazards: ["corrosive", "sensitiser"],
    routes: ["skin", "eyes", "inhalation"],
    welNote: "Wet cement is alkaline (caustic) and contains hexavalent chromium (chromate). Check SDS Section 8 for the cement dust WEL (respirable / total inhalable) in EH40.",
    controls: ["eliminate", "hygiene", "spill-kit"],
    ppeGloves: "Waterproof, chemical-resistant gloves (cotton-lined nitrile); change if wet through",
    ppeEye: "Safety glasses; goggles when mixing or pouring",
    ppeRpe: "FFP3 only when handling dry powder / sweeping; not a substitute for dust control",
    storage: "Dry, sealed bags off the ground; keep away from water until use.",
    firstAid: "Skin: wash off immediately with plenty of water. Eyes: irrigate for 15+ minutes, seek medical attention — cement burns are slow to show. Remove wet, contaminated clothing.",
    healthSurveillance: true,
    healthSurveillanceNote: "Skin / dermatitis checks for regular users; investigate any rash or cracking early — chromate sensitisation can be career-ending.",
  },
  {
    label: "Silica dust — cutting / grinding masonry, concrete, stone",
    product: "Respirable crystalline silica (RCS) dust from cutting / grinding",
    hazards: ["carcinogen", "sensitiser"],
    routes: ["inhalation"],
    welNote: "RCS Workplace Exposure Limit is very low — confirm the current EH40 figure for respirable crystalline silica. Dry cutting can exceed it many times over within minutes.",
    controls: ["eliminate", "wet-cutting", "lev", "enclosure", "reduce-time", "hygiene"],
    ppeGloves: "General-purpose as needed",
    ppeEye: "Safety glasses / goggles for cutting",
    ppeRpe: "FFP3 (or powered RPE for prolonged work) in addition to water/LEV — never instead of them. Face-fit test required for tight-fitting masks.",
    storage: "N/A — dust is generated by the task; control at source.",
    firstAid: "Move to fresh air. Persistent breathlessness or cough — seek medical advice and report exposure.",
    healthSurveillance: true,
    healthSurveillanceNote: "Lung function / respiratory health surveillance for workers regularly exposed to RCS.",
  },
  {
    label: "Expanding PU foam (isocyanates)",
    product: "Expanding polyurethane (PU) foam",
    hazards: ["sensitiser", "flammable"],
    routes: ["inhalation", "skin", "eyes"],
    welNote: "Contains isocyanates (MDI). Check SDS Section 8 for the isocyanate WEL in EH40 — there is a short-term as well as a long-term limit.",
    controls: ["eliminate", "lev", "enclosure", "reduce-time", "hygiene"],
    ppeGloves: "Nitrile gloves — cured foam is hard to remove from skin",
    ppeEye: "Safety glasses / goggles",
    ppeRpe: "FFP3 minimum; air-fed RPE for spray application. Face-fit test required.",
    storage: "Cans away from heat and ignition sources; pressurised — do not pierce or burn.",
    firstAid: "Skin: do not use solvents; mechanical removal of cured foam only. Eyes: irrigate and seek medical attention. Breathing difficulty / wheeze: fresh air and medical advice — isocyanate asthma can be triggered by low exposure.",
    healthSurveillance: true,
    healthSurveillanceNote: "Respiratory health surveillance for regular isocyanate users; report any new wheeze, cough or breathlessness.",
  },
  {
    label: "Solvent-based paints / thinners",
    product: "Solvent-based paint / thinners",
    hazards: ["flammable", "acute-toxic", "sensitiser"],
    routes: ["inhalation", "skin", "eyes"],
    welNote: "Solvent vapours (e.g. xylene, white spirit). Check SDS Section 8 for the relevant solvent WELs in EH40; vapour builds up fast in unventilated spaces.",
    controls: ["eliminate", "lev", "enclosure", "reduce-time", "hygiene", "spill-kit"],
    ppeGloves: "Solvent-resistant nitrile gloves",
    ppeEye: "Safety glasses; goggles when spraying",
    ppeRpe: "Organic-vapour RPE (A-type filter) for spraying or poorly ventilated work. Face-fit test required.",
    storage: "Cool, ventilated, fire-rated store away from ignition; keep lids closed; bund quantities.",
    firstAid: "Skin: wash with soap and water. Eyes: irrigate. Vapour overcome: fresh air; seek medical attention if drowsy or headachy.",
    healthSurveillance: false,
    healthSurveillanceNote: "",
  },
  {
    label: "Two-pack epoxy / isocyanate paint",
    product: "Two-pack epoxy / isocyanate coating",
    hazards: ["sensitiser", "flammable", "acute-toxic"],
    routes: ["inhalation", "skin", "eyes"],
    welNote: "Hardener contains isocyanates and/or epoxy sensitisers. Check SDS Section 8 for isocyanate WELs in EH40; spraying generates a fine mist that is readily inhaled.",
    controls: ["eliminate", "lev", "enclosure", "reduce-time", "hygiene"],
    ppeGloves: "Nitrile gloves; avoid skin contact with both parts",
    ppeEye: "Goggles",
    ppeRpe: "Air-fed RPE for spraying; FFP3 minimum for brush/roller in confined areas. Face-fit test required.",
    storage: "Keep parts separate until use; cool, ventilated, fire-rated store; bund.",
    firstAid: "Skin: wash promptly. Eyes: irrigate and seek medical attention. Breathing difficulty: fresh air and urgent medical advice.",
    healthSurveillance: true,
    healthSurveillanceNote: "Respiratory and skin surveillance for regular users; report new wheeze or dermatitis early.",
  },
  {
    label: "Bitumen / hot-works compounds",
    product: "Bitumen / hot-applied compound",
    hazards: ["acute-toxic", "flammable"],
    routes: ["inhalation", "skin", "eyes"],
    welNote: "Hot bitumen fume and thermal burns. Check SDS Section 8 for the bitumen fume WEL in EH40.",
    controls: ["eliminate", "lev", "reduce-time", "hygiene", "spill-kit"],
    ppeGloves: "Heat-resistant gauntlets",
    ppeEye: "Goggles / face shield against hot splashes",
    ppeRpe: "Fume RPE when working in fume; position upwind where possible. Face-fit test required for tight-fitting masks.",
    storage: "Heaters and hot kettles per the fire plan; keep away from combustibles.",
    firstAid: "Thermal burns: cool with water, do not peel off adhered bitumen — cover and get medical help. Eyes: irrigate.",
    healthSurveillance: false,
    healthSurveillanceNote: "",
  },
  {
    label: "Fuels / oils",
    product: "Fuel / oil (diesel, petrol, lubricants)",
    hazards: ["flammable", "acute-toxic", "environmental", "sensitiser"],
    routes: ["skin", "inhalation", "eyes"],
    welNote: "Used / mineral oils and fuel vapours. Check SDS Section 8 for the relevant WELs in EH40; mineral oil is a skin-cancer / dermatitis risk on prolonged contact.",
    controls: ["reduce-time", "hygiene", "spill-kit"],
    ppeGloves: "Oil-resistant nitrile gloves; avoid oily rags in pockets",
    ppeEye: "Safety glasses",
    ppeRpe: "Generally not required outdoors; vapour RPE for refuelling in enclosed spaces",
    storage: "Bunded fuel store away from drains; spill kit to hand; environmental controls for any spillage.",
    firstAid: "Skin: wash off; remove oil-soaked clothing. Ingestion: do NOT induce vomiting — seek urgent medical help. Eyes: irrigate.",
    healthSurveillance: false,
    healthSurveillanceNote: "Skin checks where prolonged contact with used / mineral oils is likely.",
  },
  {
    label: "Adhesives",
    product: "Construction adhesive / sealant",
    hazards: ["flammable", "sensitiser", "acute-toxic"],
    routes: ["inhalation", "skin", "eyes"],
    welNote: "Solvent and/or isocyanate / epoxy adhesives vary widely — check SDS Section 8 for the specific product WELs in EH40.",
    controls: ["eliminate", "lev", "reduce-time", "hygiene"],
    ppeGloves: "Nitrile gloves",
    ppeEye: "Safety glasses",
    ppeRpe: "Organic-vapour RPE for solvent adhesives in enclosed areas. Face-fit test required.",
    storage: "Ventilated store away from ignition; lids closed.",
    firstAid: "Skin: wash with soap and water. Eyes: irrigate. Vapour: fresh air.",
    healthSurveillance: false,
    healthSurveillanceNote: "",
  },
  {
    label: "Cleaning chemicals / acids",
    product: "Cleaning chemical / acid (e.g. brick acid, descaler)",
    hazards: ["corrosive", "acute-toxic", "environmental"],
    routes: ["skin", "eyes", "inhalation"],
    welNote: "Strong acids/alkalis cause burns and give off irritant vapours/mist. Check SDS Section 8 for any WEL in EH40; never mix products.",
    controls: ["eliminate", "lev", "enclosure", "hygiene", "spill-kit"],
    ppeGloves: "Chemical-resistant gauntlets",
    ppeEye: "Goggles and face shield",
    ppeRpe: "Acid-gas / vapour RPE in enclosed areas. Face-fit test required.",
    storage: "Original labelled containers, segregated from incompatibles; bunded; spill neutraliser available.",
    firstAid: "Skin/eyes: irrigate with copious water for 15+ minutes and seek medical attention. Inhalation: fresh air.",
    healthSurveillance: false,
    healthSurveillanceNote: "",
  },
  {
    label: "Plaster / gypsum",
    product: "Plaster / gypsum",
    hazards: ["sensitiser"],
    routes: ["inhalation", "eyes", "skin"],
    welNote: "Nuisance/irritant dust when mixing and sanding. Check SDS Section 8 for the inhalable dust WEL in EH40.",
    controls: ["wet-cutting", "lev", "reduce-time", "hygiene"],
    ppeGloves: "General-purpose gloves",
    ppeEye: "Safety glasses when mixing / sanding",
    ppeRpe: "FFP3 for dry mixing and sanding back",
    storage: "Dry store off the ground; keep bags sealed.",
    firstAid: "Eyes: irrigate. Skin: wash and moisturise to prevent drying/cracking.",
    healthSurveillance: false,
    healthSurveillanceNote: "",
  },
  {
    label: "Wood dust",
    product: "Wood dust (cutting / sanding / routing)",
    hazards: ["carcinogen", "sensitiser", "flammable"],
    routes: ["inhalation", "eyes"],
    welNote: "Hardwood dust is a carcinogen; softwood dust is a respiratory sensitiser. Check the current hardwood / softwood dust WELs in EH40. Fine dust is also an explosion risk.",
    controls: ["lev", "enclosure", "reduce-time", "hygiene"],
    ppeGloves: "General-purpose gloves",
    ppeEye: "Safety glasses / goggles",
    ppeRpe: "FFP3 in addition to on-tool extraction / LEV. Face-fit test required.",
    storage: "Manage accumulations of fine dust (housekeeping) to limit fire/explosion risk.",
    firstAid: "Eyes: irrigate. Persistent cough / blocked nose / wheeze: seek medical advice.",
    healthSurveillance: true,
    healthSurveillanceNote: "Respiratory health surveillance for regular exposure, especially to hardwood dust.",
  },
];

/* On-device substance library. The Substance fields minus the per-assessment id,
   plus a name (the product) and an ISO savedAt. Persisted to localStorage only —
   the data never leaves the device. */
type SubstanceFields = Omit<Substance, "id">;
interface SavedSubstance extends SubstanceFields {
  name: string;
  savedAt: string;
}

const LIBRARY_STORE_KEY = "ramsdocs.coshh.substances";

function readLibrary(): SavedSubstance[] {
  try {
    const raw = window.localStorage.getItem(LIBRARY_STORE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedSubstance[]) : [];
  } catch {
    return [];
  }
}

function writeLibrary(items: SavedSubstance[]): void {
  try {
    window.localStorage.setItem(LIBRARY_STORE_KEY, JSON.stringify(items));
  } catch {
    /* storage full / blocked — saving silently no-ops */
  }
}

let nextId = 1;
function emptySubstance(): Substance {
  return {
    id: nextId++,
    product: "",
    hazards: [],
    routes: [],
    whoExposed: "",
    welNote: "",
    controls: [],
    ppeGloves: "",
    ppeEye: "",
    ppeRpe: "",
    storage: "",
    firstAid: "",
    healthSurveillance: false,
    healthSurveillanceNote: "",
  };
}

function ghsLabel(id: GhsId): string {
  return GHS_HAZARDS.find((h) => h.id === id)?.label ?? id;
}
function routeLabel(id: RouteId): string {
  return ROUTES.find((r) => r.id === id)?.label ?? id;
}
function controlLabel(id: ControlId): string {
  return CONTROLS.find((c) => c.id === id)?.label ?? id;
}

function toDateInput(d: Date): string {
  // YYYY-MM-DD in local time for <input type="date">.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
export function CoshhAssessmentBuilder() {
  const today = useMemo(() => new Date(), []);
  const defaultReview = useMemo(() => {
    const d = new Date(today);
    d.setFullYear(d.getFullYear() + 1);
    return d;
  }, [today]);

  const [siteActivity, setSiteActivity] = useState("");
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence({
    date: toDateInput(today),
    reviewDate: toDateInput(defaultReview),
  });
  const [substances, setSubstances] = useState<Substance[]>([emptySubstance()]);
  const resultTracked = useRef(false);

  // On-device substance library. Read after mount only (hydration-safe).
  const [library, setLibrary] = useState<SavedSubstance[]>([]);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  useEffect(() => {
    setLibrary(readLibrary());
  }, []);

  // Save a card to the library, overwriting any entry with the same name
  // (case-insensitive). The per-assessment id is dropped on the way in.
  const saveToLibrary = (s: Substance) => {
    const name = s.product.trim();
    if (!name) return;
    const { id: _id, ...fields } = s;
    const entry: SavedSubstance = { ...fields, name, savedAt: new Date().toISOString() };
    setLibrary((lib) => {
      const next = [...lib.filter((e) => e.name.toLowerCase() !== name.toLowerCase()), entry];
      writeLibrary(next);
      return next;
    });
    setSavedId(s.id);
    setTimeout(() => setSavedId((cur) => (cur === s.id ? null : cur)), 2000);
  };

  // Fill the first empty card, else append a new one (mirrors addPreset).
  const fillFromLibrary = (idx: string) => {
    const entry = library[parseInt(idx, 10)];
    if (!entry) return;
    const { name: _name, savedAt: _savedAt, ...fields } = entry;
    setSubstances((ss) => {
      const blank = ss.find(
        (s) => !s.product.trim() && s.hazards.length === 0 && s.controls.length === 0 && !s.welNote.trim(),
      );
      if (blank) {
        return ss.map((s) => (s.id === blank.id ? { ...s, ...fields } : s));
      }
      return [...ss, { ...fields, id: nextId++ }];
    });
  };

  const clearLibrary = () => {
    writeLibrary([]);
    setLibrary([]);
    setConfirmClear(false);
  };

  const update = (id: number, patch: Partial<Substance>) =>
    setSubstances((ss) => ss.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const toggle = <K extends "hazards" | "routes" | "controls">(
    id: number,
    key: K,
    value: Substance[K][number],
  ) =>
    setSubstances((ss) =>
      ss.map((s) => {
        if (s.id !== id) return s;
        const list = s[key] as string[];
        const next = list.includes(value)
          ? list.filter((v) => v !== value)
          : [...list, value];
        return { ...s, [key]: next };
      }),
    );

  const applyPreset = (id: number, idx: string) => {
    const preset = PRESETS[parseInt(idx, 10)];
    if (!preset) return;
    update(id, {
      product: preset.product,
      hazards: [...preset.hazards],
      routes: [...preset.routes],
      welNote: preset.welNote,
      controls: [...preset.controls],
      ppeGloves: preset.ppeGloves,
      ppeEye: preset.ppeEye,
      ppeRpe: preset.ppeRpe,
      storage: preset.storage,
      firstAid: preset.firstAid,
      healthSurveillance: preset.healthSurveillance,
      healthSurveillanceNote: preset.healthSurveillanceNote,
    });
  };

  // Count substances that have at least a product name — the meaningful output unit.
  const namedCount = useMemo(
    () => substances.filter((s) => s.product.trim().length > 0).length,
    [substances],
  );

  // Fire the funnel event once per visit when the assessment first has content.
  useEffect(() => {
    if (namedCount > 0 && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("coshh-assessment", { substances: namedCount });
    }
  }, [namedCount]);

  // A substance is "worked through" once it has a product name, at least one
  // hazard class identified, at least one control selected and a WEL noted —
  // the COSHH essentials a competent assessor would expect for each entry.
  const firstWorked = useMemo(
    () =>
      substances.find(
        (s) =>
          s.product.trim().length > 0 &&
          s.hazards.length > 0 &&
          s.controls.length > 0 &&
          s.welNote.trim().length > 0,
      ),
    [substances],
  );

  // Core fields the assessment should not ship without. Per-substance PPE,
  // storage and first aid stay optional — proportionate to the products in use.
  const requiredItems: { label: string; done: boolean }[] = [
    ...evidenceRequiredItems(evidence),
    { label: "Site / activity", done: siteActivity.trim().length > 0 },
    { label: "Review date", done: evidence.reviewDate.trim().length > 0 },
    {
      label: "At least one substance with hazards, controls and a WEL noted",
      done: !!firstWorked,
    },
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  return (
    <div className="space-y-6">
      {/* Sticky progress + next gap, so long scrolls keep their bearings. */}
      <div className="sticky top-20 z-10">
        <CompletionPanel items={requiredItems} title="Assessment completion" />
      </div>

      {/* SDS prompt */}
      <div className="rounded-xl border border-emerald/30 bg-emerald-tint px-5 py-4 text-[0.92rem] leading-relaxed text-emerald-deep">
        <strong>Have the safety data sheet (SDS) for each product to hand.</strong> Sections 2, 4, 7 and 8 give
        you the hazards, first aid, storage and exposure limits. The SDS is the manufacturer&apos;s data — turning
        it into a task- and site-specific assessment is your job as the competent assessor.
      </div>

      {/* Assessment header */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Assessment details</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Site / activity">
            <TextInput value={siteActivity} onChange={(e) => setSiteActivity(e.target.value)} placeholder="e.g. Plot 4 — internal fit-out" />
          </Field>
        </div>
      </div>

      {/* On-device substance library picker */}
      {library.length > 0 && (
        <div className="card shadow-soft p-6">
          <Field label="From your library" hint="Substances you've saved on this device. Choosing one fills the first empty card.">
            <SelectInput
              aria-label="Saved substances"
              value=""
              onChange={(e) => fillFromLibrary(e.target.value)}
            >
              <option value="">Add a saved substance…</option>
              {library.map((entry, idx) => (
                <option key={entry.name} value={idx}>{entry.name}</option>
              ))}
            </SelectInput>
          </Field>
          <p className="mt-3 text-[0.78rem] text-muted">
            {library.length} substance{library.length === 1 ? "" : "s"} saved on this device — nothing is sent to us.{" "}
            <button
              type="button"
              onClick={() => (confirmClear ? clearLibrary() : setConfirmClear(true))}
              className="font-semibold text-emerald-deep underline hover:text-emerald"
            >
              {confirmClear ? "Really clear?" : "Clear library"}
            </button>
          </p>
        </div>
      )}

      {/* Substance blocks */}
      <div className="space-y-5">
        {substances.map((s, i) => (
          <div key={s.id} className="card shadow-soft p-6">
            <div className="flex items-center justify-between">
              <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Substance {i + 1}</span>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => saveToLibrary(s)}
                  disabled={!s.product.trim()}
                  className="text-[0.8rem] font-semibold text-emerald-deep hover:text-emerald disabled:cursor-not-allowed disabled:text-muted disabled:opacity-50"
                >
                  {savedId === s.id ? "Saved ✓" : "Save to my library"}
                </button>
                {substances.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setSubstances((ss) => ss.filter((x) => x.id !== s.id))}
                    className="text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 grid gap-4">
              <Field label="Product / substance">
                <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                  <TextInput
                    value={s.product}
                    onChange={(e) => update(s.id, { product: e.target.value })}
                    placeholder="Product name as on the label / SDS"
                  />
                  <SelectInput
                    aria-label="Common substance presets"
                    value=""
                    onChange={(e) => applyPreset(s.id, e.target.value)}
                  >
                    <option value="">Preset defaults…</option>
                    {PRESETS.map((p, idx) => (
                      <option key={p.label} value={idx}>{p.label}</option>
                    ))}
                  </SelectInput>
                </div>
              </Field>

              <Field label="GHS hazard classes" hint="From SDS Section 2 / the label pictograms. Multi-select.">
                <div className="flex flex-wrap gap-2">
                  {GHS_HAZARDS.map((h) => {
                    const on = s.hazards.includes(h.id);
                    return (
                      <button
                        key={h.id}
                        type="button"
                        onClick={() => toggle(s.id, "hazards", h.id)}
                        className={`rounded-full border px-3.5 py-1.5 text-[0.85rem] font-semibold transition-colors ${
                          on
                            ? "border-emerald bg-emerald-tint text-emerald-deep"
                            : "border-line-2 bg-white text-muted hover:border-ink hover:text-ink"
                        }`}
                        aria-pressed={on}
                      >
                        {h.label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Exposure routes" hint="How the substance can get into / harm the body.">
                <div className="flex flex-wrap gap-2">
                  {ROUTES.map((r) => {
                    const on = s.routes.includes(r.id);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => toggle(s.id, "routes", r.id)}
                        className={`rounded-full border px-3.5 py-1.5 text-[0.85rem] font-semibold transition-colors ${
                          on
                            ? "border-emerald bg-emerald-tint text-emerald-deep"
                            : "border-line-2 bg-white text-muted hover:border-ink hover:text-ink"
                        }`}
                        aria-pressed={on}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Who is exposed & for how long">
                <TextArea
                  value={s.whoExposed}
                  onChange={(e) => update(s.id, { whoExposed: e.target.value })}
                  placeholder="e.g. 2 operatives, intermittent cutting ~1 hr/day; others nearby in the same room"
                />
              </Field>

              <Field
                label="Workplace exposure limit (WEL)"
                hint="Check SDS Section 8 and HSE EH40 for the WEL. Note the figure and whether dry use is likely to exceed it."
              >
                <TextArea
                  value={s.welNote}
                  onChange={(e) => update(s.id, { welNote: e.target.value })}
                  placeholder="e.g. RCS WEL is very low — confirm the current EH40 figure; dry cutting will exceed it within minutes."
                />
              </Field>

              <Field
                label="Controls (in order of the COSHH hierarchy)"
                hint="Work down the list: eliminate/substitute first, engineering controls next, then ways of working — PPE/RPE is the last line, not the first."
              >
                <div className="space-y-2">
                  {CONTROLS.map((c) => (
                    <CheckboxRow
                      key={c.id}
                      checked={s.controls.includes(c.id)}
                      onChange={() => toggle(s.id, "controls", c.id)}
                    >
                      {c.label}
                    </CheckboxRow>
                  ))}
                </div>
              </Field>

              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Gloves (type)">
                  <TextInput value={s.ppeGloves} onChange={(e) => update(s.id, { ppeGloves: e.target.value })} placeholder="e.g. nitrile" />
                </Field>
                <Field label="Eye protection">
                  <TextInput value={s.ppeEye} onChange={(e) => update(s.id, { ppeEye: e.target.value })} placeholder="e.g. goggles" />
                </Field>
                <Field label="RPE" hint="Tight-fitting masks need a face-fit test.">
                  <TextInput value={s.ppeRpe} onChange={(e) => update(s.id, { ppeRpe: e.target.value })} placeholder="e.g. FFP3, face-fit tested" />
                </Field>
              </div>

              <Field label="Storage & disposal">
                <TextArea
                  value={s.storage}
                  onChange={(e) => update(s.id, { storage: e.target.value })}
                  placeholder="Storage conditions, segregation, bunding, waste route (SDS Sections 7 & 13)."
                />
              </Field>

              <Field label="First aid / emergency actions">
                <TextArea
                  value={s.firstAid}
                  onChange={(e) => update(s.id, { firstAid: e.target.value })}
                  placeholder="First aid by exposure route and spill / fire actions (SDS Sections 4, 5 & 6)."
                />
              </Field>

              <div className="rounded-lg border border-line bg-mist/60 p-4">
                <CheckboxRow
                  checked={s.healthSurveillance}
                  onChange={(v) => update(s.id, { healthSurveillance: v })}
                >
                  Health surveillance required (e.g. skin / dermatitis checks, lung function where relevant)
                </CheckboxRow>
                {s.healthSurveillance && (
                  <div className="mt-3">
                    <Field label="Health surveillance detail">
                      <TextArea
                        value={s.healthSurveillanceNote}
                        onChange={(e) => update(s.id, { healthSurveillanceNote: e.target.value })}
                        placeholder="What is checked, how often, and who arranges it."
                      />
                    </Field>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setSubstances((ss) => [...ss, emptySubstance()])}
          className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
        >
          + Add another substance
        </button>
      </div>

      <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />

      <GuardedPrintButton
        tool="coshh-assessment"
        label="Print / save assessment as PDF"
        missing={missingRequired}
        warningTitle="This COSHH assessment still has empty core fields"
      />

      {/* Printable assessment document */}
      <div className="print-area card shadow-soft p-6" id="coshh-record">
        <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
          COSHH assessment
        </div>
        <h2 className="mt-1 text-lg">
          {evidence.company || "Company"}
          {siteActivity ? ` — ${siteActivity}` : ""}
        </h2>

        <p className="mt-3 rounded-lg bg-mist px-4 py-3 text-[0.82rem] leading-relaxed text-muted">
          Controls are set out following the COSHH hierarchy — prevent exposure by eliminating or substituting
          the substance first; where that is not reasonably practicable, apply engineering controls (LEV,
          enclosure, dust suppression), then ways of working, with PPE/RPE as the last line of defence, never
          the first.
        </p>

        {namedCount === 0 ? (
          <p className="mt-5 text-sm text-muted">
            Add at least one product above to build the assessment record.
          </p>
        ) : (
          <div className="mt-5 space-y-6">
            {substances
              .filter((s) => s.product.trim().length > 0)
              .map((s, i) => (
                <div key={s.id} className="border-t border-line pt-4">
                  <h3 className="font-display text-base font-bold text-ink">
                    {i + 1}. {s.product}
                  </h3>
                  <dl className="mt-2 grid gap-x-4 gap-y-2 text-[0.88rem] sm:grid-cols-[170px_1fr]">
                    <dt className="font-semibold text-ink">Hazard classes</dt>
                    <dd className="text-body">{s.hazards.length ? s.hazards.map(ghsLabel).join(", ") : "—"}</dd>

                    <dt className="font-semibold text-ink">Exposure routes</dt>
                    <dd className="text-body">{s.routes.length ? s.routes.map(routeLabel).join(", ") : "—"}</dd>

                    <dt className="font-semibold text-ink">Who is exposed</dt>
                    <dd className="text-body">{s.whoExposed || "—"}</dd>

                    <dt className="font-semibold text-ink">Exposure limit (WEL)</dt>
                    <dd className="text-body">{s.welNote || "Check SDS Section 8 and EH40."}</dd>

                    <dt className="font-semibold text-ink">Controls (hierarchy order)</dt>
                    <dd className="text-body">
                      {s.controls.length ? (
                        <ol className="list-decimal space-y-0.5 pl-4">
                          {CONTROLS.filter((c) => s.controls.includes(c.id)).map((c) => (
                            <li key={c.id}>{controlLabel(c.id)}</li>
                          ))}
                        </ol>
                      ) : (
                        "—"
                      )}
                    </dd>

                    <dt className="font-semibold text-ink">PPE / RPE</dt>
                    <dd className="text-body">
                      {[
                        s.ppeGloves && `Gloves: ${s.ppeGloves}`,
                        s.ppeEye && `Eyes: ${s.ppeEye}`,
                        s.ppeRpe && `RPE: ${s.ppeRpe}`,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </dd>

                    <dt className="font-semibold text-ink">Storage & disposal</dt>
                    <dd className="text-body">{s.storage || "—"}</dd>

                    <dt className="font-semibold text-ink">First aid / emergency</dt>
                    <dd className="text-body">{s.firstAid || "—"}</dd>

                    <dt className="font-semibold text-ink">Health surveillance</dt>
                    <dd className="text-body">
                      {s.healthSurveillance
                        ? `Required${s.healthSurveillanceNote ? ` — ${s.healthSurveillanceNote}` : ""}`
                        : "Not identified as required for this substance"}
                    </dd>
                  </dl>
                </div>
              ))}
          </div>
        )}

        <p className="mt-5 text-[0.78rem] leading-relaxed text-muted">
          This builder structures a COSHH assessment; whether it is suitable and sufficient is a matter for the
          competent assessor&apos;s judgement against the actual products, tasks and people on this site. It does
          not certify COSHH compliance.
        </p>

        <EvidencePrint value={evidence} briefing />

        <PrintFooter toolName="COSHH assessment builder" />
      </div>
    </div>
  );
}
